import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListRoutingRulesResult,
  type SimulateRoutingResult,
} from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Who a new lead goes to (F-034).
 *
 * `lead.owner_id` has existed since the first records migration and nothing
 * set it on capture, so every lead arrived owned by nobody - in a queue every
 * rep assumed somebody else was working.
 *
 * Two things this panel has to say out loud. **A rule that can reach nobody**
 * is drawn as a warning on the rule itself, because it matches and assigns
 * nothing, which on the leads list looks exactly like routing not running. And
 * **there is no territory strategy**, because territories are not built: a
 * strategy in a dropdown that silently assigns nobody is worse than one that
 * is absent.
 */

const STRATEGIES = ['round-robin', 'load-balanced', 'named'] as const;
/** Leads to an owner, or cases to an agent (G-224); each has its own list and order. */
const OBJECTS = ['lead', 'support_case'] as const;
const PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;

export function RoutingPanel({
  people,
  teams,
}: {
  people: ReadonlyArray<{ userId: string; name: string }>;
  teams: ReadonlyArray<{ teamId: string; name: string }>;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState<(typeof STRATEGIES)[number]>('round-robin');
  const [teamId, setTeamId] = useState('');
  const [personId, setPersonId] = useState('');
  const [respondIn, setRespondIn] = useState('');
  const [simulated, setSimulated] = useState<SimulateRoutingResult | null>(null);
  const [object, setObject] = useState<(typeof OBJECTS)[number]>('lead');
  const [priority, setPriority] = useState('');
  const forCases = object === 'support_case';

  const rules = useQuery<ListRoutingRulesResult, ApiError>({
    queryKey: ['routing-rules', object],
    queryFn: () => api.listRoutingRules({ query: { object } }),
  });

  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ['routing-rules'] });

  const add = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveRoutingRule({
        body: {
          object,
          name: name.trim(),
          // Everything, by default. The condition builder narrows it; a rule
          // that matches nothing is a rule nobody notices is broken. A case
          // rule can be narrowed to one priority here.
          conditions: forCases && priority ? { all: [{ field: 'priority', operator: 'eq', value: priority }] } : { all: [] },
          strategy,
          targetUserId: strategy === 'named' ? personId || null : null,
          targetTeamId: strategy === 'named' ? null : teamId || null,
          /*
           * Blank means NO CLOCK, not zero. A nurture list nobody promises to
           * answer within an hour should not manufacture a breach every night
           * until somebody switches the whole thing off.
           */
          responseMinutes: forCases || respondIn.trim() === '' ? null : Number(respondIn),
        },
      }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });

  const remove = useMutation<unknown, ApiError, string>({
    mutationFn: (ruleId) => api.deleteRoutingRule({ path: { ruleId } }),
    onSuccess: () => refresh(),
  });

  const reorder = useMutation<unknown, ApiError, string[]>({
    mutationFn: (ruleIds) => api.reorderRoutingRules({ body: { ruleIds, object } }),
    onSuccess: () => refresh(),
  });

  const simulate = useMutation<SimulateRoutingResult, ApiError, string>({
    mutationFn: (leadId) => api.simulateRouting({ query: { leadId } }),
    onSuccess: (result) => setSimulated(result),
  });

  if (rules.isPending) return <Skeleton className="h-block-md w-full" />;

  const list = rules.data?.rules ?? [];
  const failure = add.error ?? remove.error ?? reorder.error ?? simulate.error;

  const move = (ruleId: string, by: number): void => {
    const ids = list.map((rule) => rule.ruleId);
    const from = ids.indexOf(ruleId);
    const to = from + by;
    if (to < 0 || to >= ids.length) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, ruleId);
    reorder.mutate(next);
  };

  return (
    <Panel data-region="routing" id="routing">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('routing.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('routing.body')}</p>
        </div>
        <div className="flex gap-1" role="group" aria-label={t('routing.object')}>
          {OBJECTS.map((key) => (
            <Button key={key} size="small" intent={object === key ? 'primary' : 'ghost'} data-routing-object={key} aria-pressed={object === key} onClick={() => { setObject(key); setSimulated(null); }}>
              {t(`routing.object.${key}`)}
            </Button>
          ))}
        </div>

        {failure ? (
          <p data-routing-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        {list.length === 0 ? (
          <p data-routing-empty className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
            {t('routing.empty')}
          </p>
        ) : (
          <ol className="flex flex-col">
            {list.map((rule) => (
              <li
                key={rule.ruleId}
                data-routing-rule={rule.ruleId}
                className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-body text-ink">{rule.name}</span>
                  <span className="text-meta text-ink-meta">
                    {t(`routing.strategy.${rule.strategy}` as 'routing.strategy.named')}
                  </span>
                  <span className="text-meta text-ink-meta">
                    {rule.targetUserName ?? rule.targetTeamName ?? t('routing.team.any')}
                  </span>
                  {rule.active ? null : (
                    <span data-routing-off={rule.ruleId} className="text-meta text-ink-meta">
                      {t('routing.off')}
                    </span>
                  )}
                  <Button intent="ghost" size="small" data-routing-up={rule.ruleId} onClick={() => move(rule.ruleId, -1)}>
                    {t('routing.up')}
                  </Button>
                  <Button intent="ghost" size="small" data-routing-down={rule.ruleId} onClick={() => move(rule.ruleId, 1)}>
                    {t('routing.down')}
                  </Button>
                  <Button intent="ghost" size="small" data-routing-remove={rule.ruleId} onClick={() => remove.mutate(rule.ruleId)}>
                    {t('routing.remove')}
                  </Button>
                </div>
                {/* A rule that matches and assigns nothing looks, on the leads
                    list, exactly like routing not running at all. */}
                {forCases ? null : rule.responseMinutes === null ? (
                  <span data-routing-no-clock={rule.ruleId} className="text-meta text-ink-meta">
                    {t('routing.respond.none')}
                  </span>
                ) : (
                  <Num data-routing-clock={rule.ruleId} className="text-meta text-ink-meta">
                    {t('routing.respond.set', {
                      values: { count: formatCount(rule.responseMinutes, format) },
                    })}
                  </Num>
                )}
                {rule.poolSize === 0 ? (
                  <span data-routing-pool-empty={rule.ruleId} className="text-meta text-warning-text">
                    {t('routing.pool.empty')}
                  </span>
                ) : (
                  <Num data-routing-pool={rule.ruleId} className="text-meta text-ink-meta">
                    {t('routing.pool', { values: { count: formatCount(rule.poolSize, format) } })}
                  </Num>
                )}
              </li>
            ))}
          </ol>
        )}

        <div className="flex flex-wrap items-end gap-2 border-t border-t-rule pt-3">
          <input
            data-routing-name
            aria-label={t('routing.rule.name')}
            placeholder={t('routing.rule.name')}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('routing.strategy')}
            <select
              data-routing-strategy
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={strategy}
              onChange={(event) => setStrategy(event.target.value as (typeof STRATEGIES)[number])}
            >
              {STRATEGIES.map((entry) => (
                <option key={entry} value={entry}>
                  {t(`routing.strategy.${entry}` as 'routing.strategy.named')}
                </option>
              ))}
            </select>
          </label>
          {strategy === 'named' ? (
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('routing.person')}
              <select
                data-routing-person
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={personId}
                onChange={(event) => setPersonId(event.target.value)}
              >
                <option value="">—</option>
                {people.map((person) => (
                  <option key={person.userId} value={person.userId}>
                    {person.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('routing.team')}
              <select
                data-routing-team
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={teamId}
                onChange={(event) => setTeamId(event.target.value)}
              >
                <option value="">{t('routing.team.any')}</option>
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {forCases ? (
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('routing.priority')}
              <select
                data-routing-priority
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                <option value="">{t('routing.priority.any')}</option>
                {PRIORITIES.map((key) => <option key={key} value={key}>{key}</option>)}
              </select>
            </label>
          ) : (
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('routing.respond')}
              <input
                data-routing-respond
                inputMode="numeric"
                placeholder={t('routing.respond.none')}
                className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={respondIn}
                onChange={(event) => setRespondIn(event.target.value)}
              />
            </label>
          )}
          <Button
            intent="secondary"
            size="small"
            data-routing-add
            disabled={name.trim() === ''}
            onClick={() => add.mutate()}
          >
            {t('routing.rule.add')}
          </Button>
        </div>

        {/* Territories are not built, so no strategy pretends to use them. */}
        <p data-routing-no-territory className="text-meta text-ink-meta">
          {t('routing.noTerritory')}
        </p>

        {forCases ? (
          <p data-routing-cases-note className="text-meta text-ink-meta">{t('routing.casesNote')}</p>
        ) : (
          <SimulatePanel
            onSimulate={(leadId) => simulate.mutate(leadId)}
            result={simulated}
          />
        )}
      </div>
    </Panel>
  );
}

/**
 * Tries the rules against a real lead without assigning it.
 *
 * A routing rule nobody can test before switching it on is a rule somebody
 * tries on live leads.
 */
function SimulatePanel({
  onSimulate,
  result,
}: {
  onSimulate: (leadId: string) => void;
  result: SimulateRoutingResult | null;
}): JSX.Element {
  const t = useT();
  const leads = useQuery({
    queryKey: ['routing-sample-leads'],
    queryFn: () => api.listLeads({ query: { limit: 5 } }),
  });
  const [leadId, setLeadId] = useState('');

  return (
    <div className="flex flex-col gap-2 border-t border-t-rule pt-3">
      <SectionLabel>{t('routing.simulate')}</SectionLabel>
      <div className="flex flex-wrap items-end gap-2">
        <select
          data-routing-sample
          aria-label={t('routing.simulate')}
          className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
          value={leadId}
          onChange={(event) => setLeadId(event.target.value)}
        >
          <option value="">—</option>
          {(leads.data?.rows ?? []).map((lead) => (
            <option key={lead.leadId} value={lead.leadId}>
              {lead.fullName}
            </option>
          ))}
        </select>
        <Button
          intent="ghost"
          size="small"
          data-routing-simulate
          disabled={leadId === ''}
          onClick={() => onSimulate(leadId)}
        >
          {t('routing.simulate')}
        </Button>
      </div>
      {result === null ? null : (
        <p data-routing-simulated={result.reason} className="text-meta text-ink">
          {result.reason === 'matched'
            ? t('routing.simulate.matched', {
                values: { rule: result.ruleName ?? '', owner: result.ownerName ?? '' },
              })
            : result.reason === 'pool-empty'
              ? t('routing.simulate.poolEmpty', { values: { rule: result.ruleName ?? '' } })
              : t('routing.simulate.noRule')}
        </p>
      )}
    </div>
  );
}
